import {openDeleteDialog, openDialog} from 'platform/components';
import {match} from 'ts-pattern';

import {isArray} from 'ramda-adjunct';

import {useDeleteRoleMutation} from '@dms/api/security';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate} from '@dms/settings/shared';
import {DmsDataGrid, handleApiError} from '@dms/shared';

import {composePath, noop, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

import {NewRoleForm} from './components/NewRoleForm';

const PREDEFINED_COLUMNS = ['title'] as const;

export function RoleOverview() {
  const navigate = useNavigate();
  const [deleteRole] = useDeleteRoleMutation();

  const createNewRole = () =>
    openDialog(<NewRoleForm />, {
      size: 'small',
      scrollBehavior: 'outside',
      title: i18n.t('page.settings.actions.newCustomRole'),
      'data-testid': testIds.settings.userManagement('newRoleModal'),
    });

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    rowId,
    refreshData,
    getColumnData,
  }) => {
    const roleId = isArray(rowId) ? rowId[0] : rowId;
    const roleName = getColumnData<string>('title')?.value;

    match(actionKey)
      .with('detail', () =>
        navigate(composePath(settingsRoutes.roleManagementDetail, {params: {roleId}}))
      )
      .with('delete', () =>
        openDeleteDialog({
          onConfirm: () =>
            deleteRole({roleId})
              .unwrap()
              .then(() => refreshData())
              .catch(handleApiError),
          text: i18n.t('page.settings.labels.deleteRoleDialog', {
            name: roleName,
          }),
          'data-testid': testIds.settings.userManagement('deleteRoleConfirmDialog'),
        })
      )
      .otherwise(noop);
  };

  return (
    <SettingsTemplate
      header={{
        title: i18n.t('general.labels.roles'),
        actions: [
          {
            type: 'button',
            title: i18n.t('page.settings.actions.newCustomRole'),
            onClick: createNewRole,
          },
        ],
      }}
      data-testid={testIds.settings.userManagement('page')}
    >
      <DmsDataGrid
        gridCode="role"
        actionCallback={actionCallback}
        predefinedColumns={PREDEFINED_COLUMNS}
        data-testid={testIds.settings.userManagement('role-datagrid')}
      />
    </SettingsTemplate>
  );
}
